import { useEffect, useState } from "react";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import type { UsageSummary } from "@/lib/types";

const PERIOD_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
};

const NUMBER_FORMAT = new Intl.NumberFormat();

export function UsageTab() {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<UsageSummary>("/usage/current")
      .then((res) => {
        if (!cancelled) setUsage(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load usage");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <ErrorBanner>{error}</ErrorBanner>;
  }

  if (!usage) {
    return (
      <div className="grid gap-3 md:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  const periodLabel = new Date(usage.period_start).toLocaleDateString(undefined, PERIOD_FORMAT);
  const isEmpty = usage.request_count === 0 && usage.by_model.length === 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <UsageCard
          title="Requests"
          subtitle={periodLabel}
          value={NUMBER_FORMAT.format(usage.request_count)}
        />
        <UsageCard
          title="Tokens"
          subtitle={`${NUMBER_FORMAT.format(usage.input_tokens)} in / ${NUMBER_FORMAT.format(usage.output_tokens)} out`}
          value={NUMBER_FORMAT.format(usage.input_tokens + usage.output_tokens)}
        />
        <UsageCard
          title="Cost"
          subtitle="USD month-to-date"
          value={`$${usage.cost_usd_total.toFixed(4)}`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By model</CardTitle>
          <CardDescription>Aggregated requests per model for {periodLabel}.</CardDescription>
        </CardHeader>
        <CardContent>
          {isEmpty ? (
            <EmptyState>No usage yet this period.</EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Requests</TableHead>
                  <TableHead className="text-right">Input tokens</TableHead>
                  <TableHead className="text-right">Output tokens</TableHead>
                  <TableHead className="text-right">Cost (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.by_model.map((row) => (
                  <TableRow key={row.model}>
                    <TableCell className="font-medium">{row.model}</TableCell>
                    <TableCell className="text-right">
                      {NUMBER_FORMAT.format(row.request_count)}
                    </TableCell>
                    <TableCell className="text-right">
                      {NUMBER_FORMAT.format(row.input_tokens)}
                    </TableCell>
                    <TableCell className="text-right">
                      {NUMBER_FORMAT.format(row.output_tokens)}
                    </TableCell>
                    <TableCell className="text-right">${row.cost_usd.toFixed(4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageCard({
  title,
  subtitle,
  value,
}: {
  title: string;
  subtitle: string;
  value: string;
}) {
  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-2xl font-semibold">{value}</div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}
